/**
 * BookingEmailStatus
 *
 * Shows SendGrid delivery status for a single booking inside the Admin
 * Booking Details modal. Read-only: renders badges + raw event list.
 *
 * Backend: GET /api/admin/bookings/:bookingId/email-status
 *
 * Note on granularity:
 *   Without SendGrid categories, we cannot split confirmation / receipt /
 *   reminder / review-request within a single recipient. We surface one
 *   "Customer" badge and one "Admin Alert" badge per booking. The raw
 *   events list is included so the admin can still audit specific messages.
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Clock, Mail, RefreshCw, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { getBookingEmailStatus } from '../utils/api';

const BADGE_STYLES = {
  delivered: {
    label: 'Delivered',
    icon: CheckCircle2,
    className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  },
  bounced: {
    label: 'Bounced',
    icon: XCircle,
    className: 'bg-red-500/15 text-red-300 border-red-500/40',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    className: 'bg-red-500/15 text-red-300 border-red-500/40',
  },
  deferred: {
    label: 'Deferred',
    icon: Clock,
    className: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  },
  processed: {
    label: 'Pending',
    icon: Clock,
    className: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  },
  unknown: {
    label: 'No data',
    icon: AlertTriangle,
    className: 'bg-gray-500/15 text-gray-300 border-gray-500/40',
  },
};

function StatusBadge({ badge, testId }) {
  const cfg = BADGE_STYLES[badge] || BADGE_STYLES.unknown;
  const Icon = cfg.icon;
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.className}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </span>
  );
}

function formatTs(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function EventRow({ ev }) {
  const type = (ev.event || '').toLowerCase();
  const isBad = ['bounce', 'dropped', 'blocked', 'spamreport'].includes(type);
  const isGood = ['delivered', 'open', 'click'].includes(type);
  const cls = isBad ? 'text-red-300' : isGood ? 'text-emerald-300' : 'text-gray-300';
  return (
    <div className="flex items-start justify-between gap-3 text-xs border-b border-white/5 py-1.5">
      <div className={`font-semibold uppercase tracking-wider ${cls}`}>{type || 'unknown'}</div>
      <div className="text-gray-400 flex-1 text-right">
        <div>{formatTs(ev.timestamp)}</div>
        {ev.reason && <div className="text-red-300/80 italic">{ev.reason}</div>}
      </div>
    </div>
  );
}

export default function BookingEmailStatus({ bookingId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showEvents, setShowEvents] = useState(false);

  const load = async () => {
    if (!bookingId) return;
    setLoading(true);
    setErr('');
    try {
      const res = await getBookingEmailStatus(bookingId);
      setData(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load email delivery status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [bookingId]);

  return (
    <div className="bg-gray-800/50 p-6 rounded-xl border border-yellow-500/20" data-testid="booking-email-status">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-yellow-400 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Email Delivery
        </h3>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="booking-email-status-refresh"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-gray-300 hover:text-white border border-gray-600 hover:border-gray-400 rounded-md transition disabled:opacity-50"
          title="Refresh delivery status"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && (
        <div className="text-sm text-gray-400" data-testid="booking-email-status-loading">Loading delivery events…</div>
      )}

      {!loading && err && (
        <div className="text-sm text-red-300 bg-red-900/20 border border-red-500/30 rounded-md px-3 py-2" data-testid="booking-email-status-error">
          {err}
        </div>
      )}

      {!loading && !err && data && (
        <div className="space-y-4">
          {/* Customer row */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm text-gray-400">Customer</div>
              <div className="text-white font-semibold break-all" data-testid="booking-email-status-customer-email">
                {data.customer?.email || '—'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge badge={data.customer?.badge || 'unknown'} testId="booking-email-status-customer-badge" />
              {data.customer?.latestEvent?.timestamp && (
                <span className="text-[11px] text-gray-500">{formatTs(data.customer.latestEvent.timestamp)}</span>
              )}
            </div>
          </div>

          {/* Admin alert row(s) */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm text-gray-400">Admin Alert</div>
              <div className="text-white font-semibold break-all text-xs" data-testid="booking-email-status-admin-recipients">
                {(data.admin?.recipients || []).join(', ') || '—'}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              <StatusBadge badge={data.admin?.badge || 'unknown'} testId="booking-email-status-admin-badge" />
              {(data.admin?.perRecipient || []).map((r) => (
                <div key={r.email} className="flex items-center gap-2 text-[11px] text-gray-400">
                  <span className="truncate max-w-[180px]" title={r.email}>{r.email}</span>
                  <StatusBadge badge={r.badge || 'unknown'} testId={`booking-email-status-admin-badge-${r.email}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Limitations note */}
          {data.limitations && (
            <div className="flex items-start gap-2 text-[11px] text-gray-400 bg-white/5 border border-white/10 rounded-md px-3 py-2">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{data.limitations}</span>
            </div>
          )}

          {/* Raw events toggle */}
          <div>
            <button
              type="button"
              onClick={() => setShowEvents((v) => !v)}
              data-testid="booking-email-status-toggle-events"
              className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white"
            >
              {showEvents ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showEvents ? 'Hide raw events' : 'Show raw events'}
            </button>
            {showEvents && (
              <div className="mt-2 space-y-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                    Customer ({(data.customer?.events || []).length})
                  </div>
                  {(data.customer?.events || []).length === 0 ? (
                    <div className="text-xs text-gray-500 italic">No events after booking creation.</div>
                  ) : (
                    (data.customer.events || []).map((ev, i) => (
                      <EventRow key={`c-${i}`} ev={ev} />
                    ))
                  )}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                    Admin ({(data.admin?.perRecipient || []).reduce((s, r) => s + (r.events?.length || 0), 0)})
                  </div>
                  {(data.admin?.perRecipient || []).flatMap((r) => r.events || []).length === 0 ? (
                    <div className="text-xs text-gray-500 italic">No events after booking creation.</div>
                  ) : (
                    (data.admin?.perRecipient || []).flatMap((r) => r.events || [])
                      .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))
                      .map((ev, i) => <EventRow key={`a-${i}`} ev={ev} />)
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
